import RippleButton from '@components/ui/RippleButton'
import Toggle from '@components/ui/Toggle'
import NumericInput from '@components/ui/NumericInput'
import { useClassicBetting } from '@hooks/betting/useClassicBetting'
import { PANELS, ACTION_VARIANT, TOGGLE } from '@constants/bettingConstants'

const isOn = (control) => control === TOGGLE.ENABLED

// Same logic as the normal card (single hook) — only the UI is compact: an
// auto-cashout toggle + input, three preset buttons + one stake input, and one
// big action button. No +/- stepper or auto-bet loop in mini.
const BettingCard = () => {
  const {
    stake,
    autoCashout,
    liveCashout,
    actionButton,
    interactionControls: ic,
    presetStakes,
    renderValue,
    currencyCode,
    setQuickStake,
    onStakeInput,
    toggleAutoCashout,
    onAutoCashoutInput,
    onAutoCashoutBlur,
    onActionButton,
  } = useClassicBetting(PANELS.ONE)

  const presets = (presetStakes ?? []).slice(0, 3)

  // PLACED (a submitted, locked-in bet) reuses the bet style and is rendered inert
  // by `actionButton.enabled` — mini has no separate treatment for it.
  const actionModifier =
    actionButton.variant === ACTION_VARIANT.CASHOUT
      ? 'standard-mini-betcard-action-button--cashout'
      : actionButton.variant === ACTION_VARIANT.CANCEL
        ? 'standard-mini-betcard-action-button--cancel'
        : 'standard-mini-betcard-action-button--bet'

  return (
    <div className="standard-mini-betcard-outer-container">
      <div className="standard-mini-betcard-auto-cashout-container">
        <div className="standard-mini-betcard-auto-cashout-label">Auto Cashout</div>
        <div className="standard-mini-betcard-auto-cashout-controls">
          <Toggle
            isChecked={autoCashout.status === TOGGLE.ENABLED}
            onCheck={toggleAutoCashout}
            disabled={!isOn(ic.autoCashout)}
            className={isOn(ic.autoCashout) ? '' : 'disable-actions'}
          />
          <NumericInput
            className={`standard-mini-betcard-auto-cashout-input ${isOn(ic.autoCashout) ? '' : 'disable-actions'}`}
            placeholder="0.00"
            value={autoCashout.value ?? ''}
            onChange={(event) => onAutoCashoutInput(event.target.value)}
            onBlur={onAutoCashoutBlur}
            disabled={!isOn(ic.autoCashout) || autoCashout.status !== TOGGLE.ENABLED}
          />
        </div>
      </div>

      <div className="standard-mini-betcard-label">
        Bet Amount{currencyCode ? ` (${currencyCode})` : ''}
      </div>

      <div className="standard-mini-betcard-controls-container">
        <div className="standard-mini-betcard-grid">
          {presets.map((value, index) => (
            <RippleButton
              key={index}
              className={`standard-mini-betcard-preset-button ${isOn(ic.quickStakes) ? '' : 'disable-actions'}`}
              onClick={() => setQuickStake(value)}
              disabled={!isOn(ic.quickStakes)}
            >
              {value}
            </RippleButton>
          ))}

          <NumericInput
            className={`standard-mini-betcard-input ${stake ? 'filled' : ''} ${isOn(ic.stakeInput) ? '' : 'disable-actions'}`}
            value={stake}
            onChange={(event) => onStakeInput(event.target.value)}
            disabled={!isOn(ic.stakeInput)}
          />
        </div>

        <RippleButton
          className={`standard-mini-betcard-action-button ${actionModifier} ${actionButton.enabled ? '' : 'disable-actions'}`}
          onClick={onActionButton}
          disabled={!actionButton.enabled}
        >
          <span className="standard-mini-betcard-action-button-title">{actionButton.label}</span>
          {actionButton.variant === ACTION_VARIANT.CASHOUT && liveCashout != null ? (
            <span className="standard-mini-betcard-action-button-amount">
              {renderValue(liveCashout)}
            </span>
          ) : null}
        </RippleButton>
      </div>
    </div>
  )
}

export default BettingCard
